import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TreePine, ShieldCheck, ChevronDown, ArrowLeft } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import LanguageSelector from "../components/LanguageSelector";
import SnowLayer from "../components/SnowLayer";
import { StarField } from "../components/Cinematic";
import { Modal } from "../components/Modal";
import { refundCopy } from "../i18n/refundCopy";
import { startCheckout } from "../lib/checkout";
import { LEGAL_LINKS } from "../i18n/legalLinks";

const track = (event, meta) => { api.post("/analytics/event", { event, meta: { ...(meta || {}), version: "v2", page: "refund" } }).catch(() => {}); };
const utms = () => { const p = new URLSearchParams(window.location.search); const o = {}; ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => { if (p.get(k)) o[k] = p.get(k); }); return o; };

const Section = ({ title, children, testid }) => (
  <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-12" data-testid={testid}>
    <h2 className="font-display text-2xl font-black uppercase leading-tight text-xmas-snow sm:text-3xl">{title}</h2>
    {children}
  </section>
);

export default function RefundPolicy() {
  const { lang, t } = useI18n();
  const C = refundCopy(lang);
  const [pricing, setPricing] = useState(null);
  const [market, setMarket] = useState("BR");
  const [faq, setFaq] = useState(-1);
  const [showSoon, setShowSoon] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); track("refund_policy_view"); }, []);
  useEffect(() => {
    const prev = document.title;
    document.title = `${C.title} — MY CHRISTMAS`;
    const m = document.createElement("meta"); m.name = "description"; m.content = C.sub; m.setAttribute("data-legal-refund", "1"); document.head.appendChild(m);
    return () => { document.title = prev; m.remove(); };
  }, [C.title, C.sub]);
  useEffect(() => {
    api.get("/pricing").then(({ data }) => {
      setPricing(data);
      const saved = localStorage.getItem("mc_market");
      setMarket((saved && data.markets[saved]) ? saved : (data.detected && data.markets[data.detected]) ? data.detected : data.fallback);
    }).catch(() => {});
  }, []);
  const current = pricing?.markets?.[market];

  const buy = () => {
    const meta = { source: "refund_policy", market, country: market, language: lang, landing_route: window.location.pathname, displayed_currency: current?.currency, displayed_price: current?.amount, ...utms() };
    track("click_buy", meta);
    startCheckout({ pricing, market, lang, meta, utm: utms(), onSoon: () => setShowSoon(true) });
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-xmas-navy text-xmas-snow" data-testid="refund-policy-page">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-xmas-gold/10 bg-xmas-navy/75 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-3 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="refund-brand"><TreePine className="h-5 w-5 text-xmas-gold sm:h-6 sm:w-6" /><span className="font-display text-[13px] font-bold tracking-widest sm:text-lg">MY CHRISTMAS</span></Link>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <LanguageSelector compact />
            <Link to="/" data-testid="refund-back" className="flex items-center gap-1 whitespace-nowrap rounded-full border border-xmas-gold/40 px-3 py-1.5 text-xs transition hover:border-xmas-gold hover:text-xmas-gold sm:px-4 sm:py-2 sm:text-sm"><ArrowLeft className="h-3.5 w-3.5" />{C.back}</Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden pt-32 pb-12 sm:pt-40 sm:pb-16" data-testid="refund-hero">
        <StarField count={40} /><SnowLayer />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-xmas-gold/40 bg-xmas-navy/60"><ShieldCheck className="h-7 w-7 text-xmas-gold" /></span>
          <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="refund-title">{C.title}</h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-xmas-champagne md:text-lg" data-testid="refund-sub">{C.sub}</p>
        </div>
      </section>

      <Section title={C.main.t} testid="refund-main">
        <p className="mt-4 text-base text-xmas-slate md:text-lg">{C.main.text}</p>
      </Section>

      <Section title={C.how.t} testid="refund-how">
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {C.how.steps.map(([name, text], i) => (
            <div key={name} className="glass rounded-2xl p-6" data-testid={`refund-step-${i}`}>
              <p className="font-display text-4xl font-black text-xmas-gold/40">0{i + 1}</p>
              <h3 className="mt-2 font-display text-base font-bold tracking-wider text-xmas-gold md:text-lg">{name}</h3>
              <p className="mt-2 text-sm text-xmas-slate">{text}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title={C.term.t} testid="refund-term">
        <div className="mt-6 rounded-3xl border border-xmas-gold/30 bg-xmas-gold/[0.06] px-6 py-8 text-center">
          <p className="font-display text-5xl font-black leading-none text-xmas-gold sm:text-6xl" data-testid="refund-term-big">{C.term.big}</p>
          <p className="mx-auto mt-4 max-w-xl text-sm text-xmas-champagne">{C.term.text}</p>
        </div>
      </Section>

      <Section title={C.request.t} testid="refund-request">
        <p className="mt-4 text-base text-xmas-slate md:text-lg">{C.request.text}</p>
      </Section>

      <Section title={C.cond.t} testid="refund-cond">
        <p className="mt-4 text-base text-xmas-slate md:text-lg">{C.cond.text}</p>
      </Section>

      <Section title={C.faq.t} testid="refund-faq">
        <div className="mt-6 space-y-3">
          {C.faq.items.map(([q, a], i) => (
            <div key={q} className="glass overflow-hidden rounded-xl">
              <button onClick={() => setFaq(faq === i ? -1 : i)} data-testid={`refund-faq-${i}`} className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-medium text-xmas-snow"><span>{q}</span><ChevronDown className={`h-4 w-4 shrink-0 text-xmas-gold transition ${faq === i ? "rotate-180" : ""}`} /></button>
              {faq === i && <p className="px-5 pb-4 text-sm text-xmas-slate">{a}</p>}
            </div>
          ))}
        </div>
      </Section>

      <section className="border-t border-xmas-gold/10 py-16 text-center sm:py-20" data-testid="refund-cta">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <h2 className="font-display text-3xl font-black uppercase leading-tight sm:text-4xl"><span className="text-xmas-gold">{C.cta.t}</span></h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-xmas-slate md:text-lg">{C.cta.text}</p>
          <button onClick={buy} data-testid="refund-cta-btn" className="btn-gold mt-8 rounded-full px-7 py-4 text-sm font-bold uppercase tracking-wider">{C.cta.btn}{current?.display ? ` — ${current.display}` : ""}</button>
        </div>
      </section>

      <footer className="border-t border-xmas-gold/10 py-10 text-center" data-testid="refund-footer">
        <p className="font-display text-lg tracking-widest">MY CHRISTMAS</p>
        <p className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-xmas-slate">
          <Link to="/terms" className="underline-offset-4 hover:text-xmas-gold hover:underline" data-testid="refund-footer-terms">{(LEGAL_LINKS[lang] || LEGAL_LINKS.pt).terms}</Link><span className="text-xmas-gold/40">|</span>
          <Link to="/privacy" className="underline-offset-4 hover:text-xmas-gold hover:underline" data-testid="refund-footer-privacy">{(LEGAL_LINKS[lang] || LEGAL_LINKS.pt).privacy}</Link>
        </p>
        <Link to="/" className="mt-3 inline-block text-xs text-xmas-slate underline-offset-4 hover:text-xmas-gold hover:underline" data-testid="refund-footer-home">{C.back}</Link>
      </footer>

      <Modal open={showSoon} onClose={() => setShowSoon(false)} title={t("checkout.soon_title")} testid="refund-checkout-soon"><p className="text-sm text-xmas-slate">{t("checkout.soon")}</p></Modal>
    </div>
  );
}
